import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/lib/siteData";

const Slideshow = ({ slides, title }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setIndex((v) => (v + 1) % slides.length),
      3500
    );
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (n) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((v) => (v + n + slides.length) % slides.length);
  };

  return (
    <div className="relative w-full aspect-[4/5]" data-testid="gallery-slideshow">
      {slides.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${title} — step ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <button
        type="button"
        onClick={go(-1)}
        aria-label="Previous photo"
        data-testid="gallery-slide-prev"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-green-800 flex items-center justify-center shadow-md transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={go(1)}
        aria-label="Next photo"
        data-testid="gallery-slide-next"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-green-800 flex items-center justify-center shadow-md transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIndex(i);
            }}
            aria-label={`Go to photo ${i + 1}`}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === index ? "bg-white" : "bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export const Gallery = () => {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 sm:py-28 lg:py-32 bg-white"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
            Our work
          </span>
          <h2
            data-testid="gallery-headline"
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-green-950 tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Recent projects.
          </h2>
          <p className="mt-4 text-slate-600 text-base lg:text-lg">
            A selection of real pond and garden work from across East Grinstead and Sussex.
          </p>
        </div>

        <div className="columns-1 sm:columns-2 gap-5 [column-fill:_balance]">
          {GALLERY.map((g) => (
            <figure
              key={g.id}
              data-testid={`gallery-item-${g.id}`}
              className="gallery-item group relative mb-5 break-inside-avoid rounded-2xl overflow-hidden ring-1 ring-green-100"
            >
              {g.slides ? (
                <Slideshow slides={g.slides} title={g.title} />
              ) : (
                <img
                  src={g.src}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]"
                />
              )}
              <span className="absolute top-3 left-3 z-10 text-[10px] uppercase tracking-[0.15em] font-bold text-green-800 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full shadow-sm">
                {g.tag}
              </span>
              <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-t from-green-950/85 to-transparent" />
                <div
                  className="relative text-white text-base font-semibold"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {g.title}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
